/* The Policy Studio teams you work on — scoping, design, validation — and so
   the tags that reach you: your own, and `@scoping` and the like for each of
   your teams. Shared, so the conversation's "Your teams" picker, its
   Mentions tab, tag highlights and the live feed agree at once.

   ponytail: you say which teams you are on; there is no team directory to
   read it from. Upgrade: a team claim on the Auth0 user, read here in place
   of the picker. */
import type { PolicyRole, StudioMember } from '@worker/policy-builder/client'
import { createSharedComposable, useDocumentVisibility, whenever } from '@vueuse/core'
import { policyRegistry } from '~/utils/policy-studio/registry'

/** A team as a tag: what a comment's `mentions` holds when it tags the team. */
export const teamMention = (team: PolicyRole): string => `team:${team}`

/** Reads and removes a value this browser kept before it moved to the server. */
export const takeLegacy = <T>(key: string, remove = true): T | null => {
  try {
    const raw = localStorage.getItem(key)
    if (remove) localStorage.removeItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

/** Drops a migrated browser value once the server holds it. */
export const dropLegacy = (key: string): void => {
  try {
    localStorage.removeItem(key)
  } catch {
    /* Storage blocked: nothing to drop. */
  }
}

/** You, as the registry keeps you — your teams and what you have read — so
 *  both follow you to any device. Read on sign-in and whenever the tab comes
 *  back; a change applies here at once and is saved behind it. */
export const useStudioMember = createSharedComposable(() => {
  const { user } = useUserSession()
  const { $groundcover } = useNuxtApp()
  const member = ref<StudioMember | null>(null)
  const report = (error: unknown) =>
    $groundcover?.captureException(error as Error, { area: 'policy-studio' })
  const load = async () => {
    if (!import.meta.client || !user.value) return
    try {
      member.value = await policyRegistry.me()
    } catch (error) {
      report(error)
    }
  }
  watch(() => user.value?.sub, load, { immediate: true })
  watch(useDocumentVisibility(), (state) => state === 'visible' && load())
  return { member, report }
})

export const useStudioTeams = createSharedComposable(() => {
  const { user } = useUserSession()
  const { attempt } = useStudioAction()
  const { member } = useStudioMember()

  const save = async (next: PolicyRole[]) => {
    if (!member.value) return
    member.value = { ...member.value, teams: next }
    await attempt(() => policyRegistry.setTeams(next), 'Your teams were not saved')
  }

  /* Once: a choice this browser kept before teams moved to the server. */
  whenever(
    member,
    (loaded) => {
      const legacy = takeLegacy<PolicyRole[]>(
        `admin:policy-studio:teams:${user.value?.sub ?? 'anon'}`,
      )
      if (loaded.teams === null && legacy?.length) void save(legacy)
    },
    { immediate: true, once: true },
  )

  const teams = computed<PolicyRole[]>({
    get: () => member.value?.teams ?? [],
    set: (next) => void save(next),
  })
  const tags = computed(() => [
    user.value?.email?.toLowerCase() ?? '',
    ...teams.value.map(teamMention),
  ])
  return { teams, tags }
})
